import React, { useState, useRef, useCallback } from 'react';

export default function ImageCropper({ src, onCrop, onCancel, shape = 'square' }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 200 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext('2d');
    canvas.width = 300;
    canvas.height = 300;
    ctx.clearRect(0, 0, 300, 300);
    ctx.drawImage(img, 0, 0, 300, 300);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, 300, 300);
    ctx.save();
    ctx.beginPath();
    const {x, y, size} = crop;
    if (shape === 'circle') {
      ctx.arc(x + size/2, y + size/2, size/2, 0, Math.PI * 2);
    } else {
      ctx.roundRect(x, y, size, size, shape === 'rectangle' ? 8 : 12);
    }
    ctx.clip();
    ctx.drawImage(img, 0, 0, 300, 300);
    ctx.restore();
    ctx.strokeStyle = '#f5a623';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(x + size/2, y + size/2, size/2, 0, Math.PI * 2);
    } else {
      ctx.roundRect(x, y, size, size, 12);
    }
    ctx.stroke();
  }, [crop, shape, imgLoaded]);

  React.useEffect(() => { draw(); }, [draw]);

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging(true);
    setStartPos({ x: e.clientX - rect.left - crop.x, y: e.clientY - rect.top - crop.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(300 - crop.size, e.clientX - rect.left - startPos.x));
    const ny = Math.max(0, Math.min(300 - crop.size, e.clientY - rect.top - startPos.y));
    setCrop(c => ({ ...c, x: nx, y: ny }));
  };

  const handleCrop = () => {
    const img = imgRef.current;
    const out = document.createElement('canvas');
    const size = 300;
    out.width = size; out.height = size;
    const ctx = out.getContext('2d');
    const scaleX = img.naturalWidth / 300;
    const scaleY = img.naturalHeight / 300;
    ctx.drawImage(img, crop.x * scaleX, crop.y * scaleY, crop.size * scaleX, crop.size * scaleY, 0, 0, size, size);
    out.toBlob(blob => onCrop(blob), 'image/jpeg', 0.92);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-dark-900 rounded-2xl p-5 w-full max-w-sm">
        <h3 className="text-white font-semibold mb-3">Crop Image</h3>
        <div className="relative" style={{width:300,height:300,margin:'0 auto'}}>
          <canvas ref={canvasRef} width={300} height={300} className="rounded-xl cursor-move"
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={() => setDragging(false)} onMouseLeave={() => setDragging(false)} />
          <img ref={imgRef} src={src} className="hidden" onLoad={() => setImgLoaded(true)} alt="" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-dark-400">Size: {Math.round(crop.size)}px</label>
          <input type="range" min="50" max="290" value={crop.size} onChange={e => setCrop(c => ({...c, size: parseInt(e.target.value)}))}
            className="w-full mt-1" />
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={onCancel} className="flex-1 py-2 bg-dark-700 text-dark-300 rounded-lg text-sm">Cancel</button>
          <button onClick={handleCrop} className="flex-1 py-2 bg-primary-500 text-dark-900 rounded-lg text-sm font-medium">Apply Crop</button>
        </div>
      </div>
    </div>
  );
}
